import { useMemo } from 'react';
import { Typography } from '@mui/material';

import { TalentSearch } from 'src/types/resources/TalentSearch';
import TalentPresenter from 'src/presenters/TalentPresenter';
import DataTable from 'src/components/DataTable';
import TalentInfoCell from 'src/components/DataTable/components/TalentInfoCell';
import { formatCurrency } from 'src/utils/formatCurrency';
import checkCircleIcon from 'src/assets/images/checkCircleIcon.svg';

export type TalentsTableProps = {
  talents: TalentSearch[];
  selectedTalentId: TalentSearch['id'] | undefined;
  onTalentSelect: (talent: TalentSearch | null) => void;
  loading: boolean;
};

const TalentsTable: React.FunctionComponent<TalentsTableProps> = props => {
  const { talents, selectedTalentId, onTalentSelect, loading } = props;

  const columns = useMemo(
    () => [
      {
        name: 'spacer',
        head: '',
        sortable: false,
        width: 24,
        renderCell: () => <></>,
      },
      {
        name: 'full_name',
        head: 'Name',
        sortable: false,
        renderCell: (talent: TalentSearch) => (
          <TalentInfoCell talent={TalentPresenter.transformSearchedToShort(talent)} showLocation />
        ),
      },
      {
        name: 'talent_hourly_rate',
        head: 'Talent Hourly Rate',
        sortable: false,
        renderCell: (talent: TalentSearch) => {
          const hourlyRate = parseFloat(talent.talentHourlyRate);
          return (
            <Typography>
              {!Number.isNaN(hourlyRate) ? TalentPresenter.hourlyRate({ talentHourlyRate: hourlyRate }) : '-'}
            </Typography>
          );
        },
        width: 150,
      },
      {
        name: 'org_billable_rate',
        head: 'Hourly Bill Rate',
        sortable: false,
        renderCell: (talent: TalentSearch) => {
          const billRate = parseFloat(talent.orgBillableRate);
          return <Typography>{!Number.isNaN(billRate) ? `${formatCurrency(billRate)}/hour` : '-'}</Typography>;
        },
        width: 140,
      },
      {
        name: 'select',
        head: '',
        sortable: false,
        width: 40,
        sx: { verticalAlign: 'middle' },
        renderCell: (talent: TalentSearch) =>
          talent.id === selectedTalentId ? <img src={checkCircleIcon} alt="selected" width={24} height={24} /> : null,
      },
    ],
    [selectedTalentId],
  );

  return (
    <DataTable<TalentSearch>
      loading={loading}
      columns={columns}
      resources={talents}
      noResourcesText="No results found..."
      onRowClick={onTalentSelect}
      getRowSx={talent => (talent.id === selectedTalentId ? { backgroundColor: theme => theme.palette.orange11 } : {})}
      tableSx={{
        '& .MuiTableCell-root': { borderBottom: 'none' },
        '& .MuiTableBody-root .MuiTableRow-root:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
      }}
    />
  );
};

export default TalentsTable;
